import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './RecruitersManager.css';
import { BASE_URL } from '../api';


const RecruitersManager = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    description: '',
    industry: 'Technology',
    location: {
      city: '',
      state: '',
      country: ''
    },
    contactEmail: '',
    contactPhone: '',
    established: '',
    employeeCount: '',
    isActive: true,
    featured: false,
    socialMedia: {
      linkedin: '',
      twitter: '',
      facebook: ''
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, featured
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');

  useEffect(() => {
    fetchRecruiters();
  }, []);

  const fetchRecruiters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/recruiters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiters(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch recruiters');
      console.error('Error fetching recruiters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'location' || key === 'socialMedia') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append logo file if selected
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      }

      if (editingRecruiter) {
        await axios.put(`${BASE_URL}/recruiters/${editingRecruiter._id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
        toast.success('Recruiter updated successfully');
      } else {
        await axios.post(`${BASE_URL}/recruiters`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
        toast.success('Recruiter added successfully');
      }

      resetForm();
      fetchRecruiters();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save recruiter');
      console.error('Error saving recruiter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (recruiter) => {
    setEditingRecruiter(recruiter);
    setFormData({
      companyName: recruiter.companyName || '',
      website: recruiter.website || '',
      description: recruiter.description || '',
      industry: recruiter.industry || 'Technology',
      location: recruiter.location || { city: '', state: '', country: '' },
      contactEmail: recruiter.contactEmail || '',
      contactPhone: recruiter.contactPhone || '',
      established: recruiter.established || '',
      employeeCount: recruiter.employeeCount || '',
      isActive: recruiter.isActive !== undefined ? recruiter.isActive : true,
      featured: recruiter.featured || false,
      socialMedia: recruiter.socialMedia || { linkedin: '', twitter: '', facebook: '' }
    });
    setLogoPreview(recruiter.logo ? `${BASE_URL.replace('/api', '')}${recruiter.logo}` : '');
    setShowForm(true);
  };

  const handleDelete = async (id, companyName) => {
    if (window.confirm(`Are you sure you want to delete ${companyName}?`)) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${BASE_URL}/recruiters/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recruiter deleted successfully');
        fetchRecruiters();
      } catch (error) {
        toast.error('Failed to delete recruiter');
        console.error('Error deleting recruiter:', error);
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${BASE_URL}/recruiters/${id}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Recruiter ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchRecruiters();
    } catch (error) {
      toast.error('Failed to toggle status');
      console.error('Error toggling status:', error);
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${BASE_URL}/recruiters/${id}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Recruiter ${currentFeatured ? 'removed from' : 'marked as'} featured`);
      fetchRecruiters();
    } catch (error) {
      toast.error('Failed to toggle featured status');
      console.error('Error toggling featured:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      website: '',
      description: '',
      industry: 'Technology',
      location: { city: '', state: '', country: '' },
      contactEmail: '',
      contactPhone: '',
      established: '',
      employeeCount: '',
      isActive: true,
      featured: false,
      socialMedia: { linkedin: '', twitter: '', facebook: '' }
    });
    setLogoFile(null);
    setLogoPreview('');
    setEditingRecruiter(null);
    setShowForm(false);
  };

  const filteredRecruiters = recruiters.filter(recruiter => {
    let matches = true;
    
    if (filter === 'active') {
      matches = recruiter.isActive;
    } else if (filter === 'featured') {
      matches = recruiter.featured;
    }
    
    if (industryFilter) {
      matches = matches && recruiter.industry === industryFilter;
    }
    
    if (searchTerm) {
      matches = matches && (
        recruiter.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recruiter.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return matches;
  });

  const industries = [...new Set(recruiters.map(r => r.industry).filter(Boolean))];

  return (
    <div className="recruiters-manager">
      <div className="page-header">
        <h1>Our Esteemed Recruiters</h1>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add New Recruiter
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search recruiters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Recruiters</option>
            <option value="active">Active Only</option>
            <option value="featured">Featured Only</option>
          </select>
        </div>
        <div className="filter-group">
          <select 
            value={industryFilter} 
            onChange={(e) => setIndustryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Industries</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingRecruiter ? 'Edit Recruiter' : 'Add New Recruiter'}</h2>
              <button className="close-btn" onClick={resetForm}>&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="recruiter-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Company Logo *</label>
                  <div className="logo-upload">
                    {logoPreview && (
                      <img src={logoPreview} alt="Logo preview" className="logo-preview" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      required={!editingRecruiter}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Website *</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    maxLength="500"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Industry *</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Technology">Technology</option>
                    <option value="Finance">Finance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Consulting">Consulting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Contact Email *</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Established Year</label>
                  <input
                    type="number"
                    name="established"
                    value={formData.established}
                    onChange={handleInputChange}
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </div>

                <div className="form-group">
                  <label>Employee Count</label>
                  <input
                    type="text"
                    name="employeeCount"
                    value={formData.employeeCount}
                    onChange={handleInputChange}
                    placeholder="e.g., 1000-5000"
                  />
                </div>

                <div className="form-section full-width">
                  <h3>Location</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="location.city"
                        value={formData.location.city}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="location.state"
                        value={formData.location.state}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="location.country"
                        value={formData.location.country}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section full-width">
                  <h3>Social Media</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>LinkedIn</label>
                      <input
                        type="url"
                        name="socialMedia.linkedin"
                        value={formData.socialMedia.linkedin}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/company/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Twitter</label>
                      <input
                        type="url"
                        name="socialMedia.twitter"
                        value={formData.socialMedia.twitter}
                        onChange={handleInputChange}
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Facebook</label>
                      <input
                        type="url"
                        name="socialMedia.facebook"
                        value={formData.socialMedia.facebook}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                    />
                    Featured
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingRecruiter ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recruiters Grid */}
      <div className="recruiters-grid">
        {loading && !recruiters.length ? (
          <div className="loading">Loading recruiters...</div>
        ) : filteredRecruiters.length === 0 ? (
          <div className="no-data">No recruiters found</div>
        ) : (
          filteredRecruiters.map(recruiter => (
            <div key={recruiter._id} className={`recruiter-card ${!recruiter.isActive ? 'inactive' : ''}`}>
              <div className="card-header">
                <img 
                  src={`${BASE_URL.replace('/api', '')}${recruiter.logo}`} 
                  alt={recruiter.companyName}
                  className="company-logo"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/100x100?text=Logo';
                  }}
                />
                <div className="company-info">
                  <h3>{recruiter.companyName}</h3>
                  <span className={`badge ${recruiter.industry?.toLowerCase()}`}>
                    {recruiter.industry}
                  </span>
                  {recruiter.featured && <span className="badge featured">Featured</span>}
                  {!recruiter.isActive && <span className="badge inactive">Inactive</span>}
                </div>
              </div>
              
              <div className="card-body">
                <p className="description">{recruiter.description}</p>
                <div className="details">
                  <a href={recruiter.website} target="_blank" rel="noopener noreferrer" className="website-link">
                    🌐 {recruiter.website?.replace(/^https?:\/\//, '')}
                  </a>
                  <p className="contact">📧 {recruiter.contactEmail}</p>
                  {recruiter.contactPhone && <p className="contact">📞 {recruiter.contactPhone}</p>}
                  {recruiter.location && (
                    <p className="location">
                      📍 {[recruiter.location.city, recruiter.location.state, recruiter.location.country]
                        .filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="card-actions">
                <button 
                  className="action-btn edit"
                  onClick={() => handleEdit(recruiter)}
                  title="Edit"
                >
                  ✏️
                </button>
                <button 
                  className="action-btn toggle"
                  onClick={() => handleToggleStatus(recruiter._id, recruiter.isActive)}
                  title={recruiter.isActive ? 'Deactivate' : 'Activate'}
                >
                  {recruiter.isActive ? '🔴' : '🟢'}
                </button>
                <button 
                  className="action-btn star"
                  onClick={() => handleToggleFeatured(recruiter._id, recruiter.featured)}
                  title={recruiter.featured ? 'Remove Featured' : 'Mark Featured'}
                >
                  {recruiter.featured ? '⭐' : '☆'}
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleDelete(recruiter._id, recruiter.companyName)}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecruitersManager;